import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Upload,
  Image,
  message,
  Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import TinyMCEEditor from "../../components/TinyMCEEditor";
import { BlogModalProps } from "../../types/blog";
import blogService from "../../services/blogService";
import { IMAGE_UPLOAD_ACCEPT, validateImageFile } from "../../utils/upload";

const { Option } = Select;

const BlogModal: React.FC<BlogModalProps> = ({
  visible,
  form,
  onFinish,
  onCancel,
  editingRecord,
  categories = [],
  isModalLoading,
  setIsModalLoading,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>(
    editingRecord?.thumbnail || ""
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editingRecord?.thumbnail) {
      setThumbnailUrl(editingRecord.thumbnail);
    } else {
      setThumbnailUrl("");
    }
  }, [editingRecord]);

  const handleEditorChange = (content: string) => {
    form.setFieldsValue({ content: content });
  };

  const handleFileSelect = (info: any) => {
    const file = info.file.originFileObj;
    if (file) {
      setSelectedFile(file);
      const previewUrl = URL.createObjectURL(file);
      setThumbnailUrl(previewUrl);
    }
  };

  const handleThumbnailRemove = () => {
    setThumbnailUrl("");
    setSelectedFile(null);
    form.setFieldsValue({ thumbnail: undefined });
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsModalLoading(true);

      let thumbnailUrl = values.thumbnail;

      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("fileCategory", "blog");

        const response = await blogService.uploadImage(formData);
        thumbnailUrl = response;
      }

      const formData = {
        ...values,
        thumbnail: thumbnailUrl,
      };

      await onFinish(formData);
    } catch (error) {
      setUploading(false);
      console.error("Lỗi khi lưu bài đăng: ", error);
      message.error("Lỗi khi lưu bài đăng: " + error);
      setIsModalLoading(false);
    }
  };

  const uploadProps = {
    name: "file",
    customRequest: ({ file, onSuccess }: any) => {
      onSuccess(file);
    },
    onChange: handleFileSelect,
    showUploadList: false,
    accept: IMAGE_UPLOAD_ACCEPT,
    beforeUpload: (file: File) => validateImageFile(file),
  };

  return (
    <Modal
      title={editingRecord ? "Chỉnh sửa bài đăng" : "Thêm bài đăng"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Spin spinning={isModalLoading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[{ required: true, message: "Tiêu đề không được để trống" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="blogCategoryId"
            label="Danh mục"
            rules={[{ required: true, message: "Danh mục không được để trống" }]}
          >
            <Select placeholder="Chọn danh mục">
              {categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="thumbnail"
            label="Hình ảnh đại diện"
            rules={[{ required: true, message: "Thumbnail không được để trống" }]}
          >
            <div>
              {thumbnailUrl ? (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <Image
                      src={thumbnailUrl}
                      alt="Thumbnail preview"
                      style={{
                        maxWidth: 200,
                        maxHeight: 200,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                      }}
                    />
                    <Space direction="vertical">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleThumbnailRemove}
                      >
                        Xóa
                      </Button>
                    </Space>
                  </div>
                </div>
              ) : (
                <Upload {...uploadProps}>
                  <Button icon={<PlusOutlined />}>Chọn hình ảnh</Button>
                </Upload>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="published"
            label="Trạng thái"
            initialValue={editingRecord?.published || true}
          >
            <Switch
              defaultChecked
              checkedChildren="Hiển thị"
              unCheckedChildren="Ẩn"
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Nội dung"
            rules={[{ required: true, message: "Nội dung không được để trống" }]}
          >
            <TinyMCEEditor
              apiKey={process.env.REACT_APP_EDITOR_KEY}
              initialValue={editingRecord?.content || ""}
              onChange={handleEditorChange}
              height={300}
              placeholder="Nội dung"
              type="blog"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? "Đang lưu..." : "Lưu"}
              </Button>
              <Button onClick={onCancel} disabled={uploading}>
                Huỷ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default BlogModal;
